import React, { createContext, useState, useEffect, useContext } from 'react';
import Cookies from 'js-cookie';
import { AUTH_TOKEN } from '../constants';
import { api } from '../api';
import { LoginResponse, User } from '../models';
import { AxiosResponse } from 'axios';
import jwt from 'jwt-decode';
import { isBrowser } from '../utils/isBrowser';
import { codealoCourseService } from '../vendors/codealo-courses';

interface ContextData {
  isAuthenticated: boolean;
  loggedInUser: User;
  logout: any;
  loadUserFromCookies: any;
  updateUser: any;
  loginTest: any;
  loading: boolean;
  saving: boolean;
}

export const AuthContext = createContext<ContextData>({
  loggedInUser: null,
  isAuthenticated: false,
  updateUser: () => null,
  logout: () => null,
  loadUserFromCookies: () => null,
  loginTest: () => null,
  loading: false,
  saving: false,
});

export const AuthProvider = ({ children }) => {
  let cookieUser = null;
  if (isBrowser()) {
    const token = Cookies.get(AUTH_TOKEN);
    if (token) {
      cookieUser = jwt(token);
    }
  }
  const [loggedInUser, setLoggedInUser] = useState<User | null>(cookieUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadUserFromCookies = async () => {
    const token = Cookies.get(AUTH_TOKEN);
    try {
      setLoading(true);
      if (token) {
        api.setBearerToken(token);
        codealoCourseService.setBearerToken(token);
        let response = await api.getLoggedInUser();
        //@ts-ignore
        setLoggedInUser(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      logout();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    Cookies.remove(AUTH_TOKEN);
    setLoggedInUser(null);
  };

  useEffect(() => {
    loadUserFromCookies();
  }, []);

  const loginTest = async () => {
    try {
      setLoading(true);
      const response: AxiosResponse<LoginResponse> = await api.loginTestUser();
      Cookies.set(AUTH_TOKEN, response.data.jwt, { expires: 60000 });
      api.setBearerToken(response.data.jwt);
      codealoCourseService.setBearerToken(response.data.jwt);
      setLoggedInUser(response.data.user);
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (update) => {
    try {
      setSaving(true);
      const response = await api.updateUser(loggedInUser.id, update);
      setLoggedInUser(response.data);
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!loggedInUser,
        loggedInUser,
        updateUser,
        logout,
        loadUserFromCookies,
        loginTest,
        loading,
        saving,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  const context = useContext(AuthContext);
  return context;
}
